import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { Params, Router } from '@angular/router'

import { LinkType } from '../../../../common/enums/link-type.enum'
import { YieldType } from '../../../../common/enums/yield-type.enum'
import { ActionButton } from '../../../../common/interfaces/action-button.interface'
import { DropdownLink } from '../../../../common/interfaces/dropdown-link.interface'
import { OrderByChangedEvent } from '../../../../common/interfaces/order-by-changed-event.interface'
import { ResourceDefinition } from '../../../../common/interfaces/resource-definition.interface'
import { Yield } from '../../../../common/interfaces/yield.interface'
import { AuthService } from '../../../../common/services/auth.service'
import { FlashMessageService } from '../../../../common/services/flash-message.service'
import { ResourceService } from '../../../../common/services/resource.service'
import { PolicyService } from '../../../services/policy.service'

@Component({
  selector: 'abc-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent implements OnInit {
  @Input() items: any[]
  @Input() definition: ResourceDefinition
  @Input() hiddenProps: string[] = []
  @Input() dropdownLinks: DropdownLink[]
  @Input() orderByDesc = false
  @Input() orderBy: string
  @Input() allowOrderBy = true
  @Input() editLinkQueryParams: Params
  @Input() redirectToAfterDuplicate: string

  @Output() customEventEmitter: EventEmitter<any> = new EventEmitter()
  @Output() reloadPrompted: EventEmitter<void> = new EventEmitter()
  @Output()
  orderByChanged: EventEmitter<OrderByChangedEvent> = new EventEmitter()

  yields: Yield[]

  formattedItems: { [key: string]: any }[]

  itemToDelete: any
  deletePath: string

  YieldType = YieldType

  constructor(
    private authService: AuthService,
    private policyService: PolicyService,
    private resourceService: ResourceService,
    private flashMessageService: FlashMessageService,
    private router: Router
  ) {}

  async ngOnInit() {
    const permissions = await this.authService.getPermissions()

    // JSON Parse and Stringify to remove reference between clone and original array.
    this.yields = this.definition.yields.map((y: Yield) => Object.assign({}, y))

    this.yields = this.hiddenProps.length
      ? this.yields.filter(
          (y) =>
            !this.hiddenProps.find((hiddenProp) => hiddenProp === y.property)
        )
      : this.yields

    this.yields.forEach((y: Yield) => {
      if (!this.allowOrderBy) {
        y.disableOrderBy = true
      }
    })

    this.items.forEach((item) => {
      if (this.definition.defaultLink === LinkType.EDIT) {
        item.link = [
          `/${this.definition.path || this.definition.slug}`,
          item.id.toString(),
          'edit'
        ]
      } else if (this.definition.defaultLink === LinkType.DETAIL) {
        item.link = [
          `/${this.definition.path || this.definition.slug}`,
          item.id.toString()
        ]
      } else {
        item.link = null
      }

      item.yields = []
      // Create object with values to be displayed.
      this.yields.forEach((y: Yield) => {
        const itemYield: Yield = { ...y }
        itemYield.propertyValue = this.getValue(item, itemYield.property)
        itemYield.secondPropertyValue = this.getValue(
          item,
          itemYield.secondProperty
        )
        itemYield.thirdPropertyValue = this.getValue(
          item,
          itemYield.thirdProperty
        )
        itemYield.forthPropertyValue = this.getValue(
          item,
          itemYield.forthProperty
        )

        if (typeof itemYield.link === 'function') {
          itemYield.link = itemYield.link(item)
        }

        item.yields.push(itemYield)
      })

      // Action buttons.
      item.actionButtons = []
      this.definition.actionButtons?.forEach(
        async (actionButton: ActionButton) => {
          if (
            (!actionButton.condition || actionButton.condition(item)) &&
            (!actionButton.permission ||
              permissions.includes(actionButton.permission)) &&
            (!actionButton.policy ||
              (await this.policyService.hasUserPolicyAccess(
                actionButton.policy.resourceSlug,
                actionButton.policy.resourceId(item),
                actionButton.policy.name
              )))
          ) {
            item.actionButtons.push(actionButton)
          }
        }
      )

      // Check if item can be deleted.
      if (this.definition.childrenThatPreventDelete?.length) {
        this.definition.childrenThatPreventDelete.forEach(
          (children: { propName: string; preventDeleteMessage: string }) => {
            if (
              item[children.propName] &&
              (item[children.propName] > 0 || item[children.propName].length)
            ) {
              item.preventDeleteMessage = children.preventDeleteMessage
            }
          }
        )
      }
    })
    // We make the loop on formattedItems instead of items to prevent DOM from creating before finishing format operations.
    this.formattedItems = this.items
  }

  // Emit event on changing orderBy or orderByDesc.
  order(yieldName: Yield) {
    if (yieldName.disableOrderBy) {
      return
    }

    const property = yieldName.orderByProperty || yieldName.property

    if (this.orderBy === property) {
      this.orderByDesc = !this.orderByDesc
    }
    this.orderBy = property

    this.orderByChanged.emit({
      orderBy: this.orderBy,
      orderByDesc: this.orderByDesc
    })
  }

  // Recursive getter to retrieve nested properties.
  getValue(item: any, propName?: string): any {
    let value: any

    try {
      value = propName.split('.').reduce((prev, current) => prev[current], item)
    } catch (error) {
      value = null
    }
    return value
  }

  openConfirmDeleteModal(deleteEvent: {
    itemId: number
    deletePath: string
  }): void {
    this.itemToDelete = this.items.find((i) => i.id === deleteEvent.itemId)
    this.deletePath = deleteEvent.deletePath
  }

  goToLink(path: string[] | string, isDisabled: boolean): void {
    if (!path || isDisabled) {
      return
    }

    // Separate queryParams from linkPath to use in Angular Router.
    if (typeof path === 'string' && path.includes('?')) {
      const urlArray: string[] = path.split('?')
      const pathWithoutParams = urlArray[0]
      const stringParams: string = urlArray[1]
      const queryParams = JSON.parse(
        '{"' +
          decodeURI(stringParams)
            .replace(/"/g, '\\"')
            .replace(/&/g, '","')
            .replace(/=/g, '":"') +
          '"}'
      )
      this.router.navigate([pathWithoutParams], { queryParams })
    } else {
      this.router.navigate(typeof path === 'string' ? [path] : path)
    }
  }

  triggerCustomAction(
    actionButton: ActionButton | DropdownLink,
    item: any
  ): void {
    const linkAction = actionButton.linkAction && actionButton.linkAction(item)
    const patchAction =
      actionButton.patchAction && actionButton.patchAction(item)

    if (linkAction) {
      this.router.navigate([linkAction.path], {
        queryParams: linkAction.queryParams
      })
    } else if (patchAction) {
      this.resourceService
        .patch(patchAction.resourceName, patchAction.id, patchAction.suffix)
        .subscribe(
          (res) => {
            this.flashMessageService.success(patchAction.successMessage)
            this.reloadPrompted.emit()
          },
          (err) => {
            this.flashMessageService.error(patchAction.errorMessage)
          }
        )
    }
  }
}
